// The rules. No canvas, no Vue, no audio — just state and time, so the whole
// game is testable without a browser and the renderer can be replaced without
// touching a rule.

import { makeBoard, DIRS, key, opposite, sameDir } from './maze.js';
import {
  difficultyById, decide, rafSpeed, frightSeconds, waveSeconds, NICK_SPEED,
  MAX_GHOSTS, MAX_EXTRA_PIES, speedById, SPEEDS,
} from './ai.js';

export { MAX_GHOSTS, MAX_EXTRA_PIES, SPEEDS };

export const PIZZA_POINTS = 10;
export const PIE_POINTS = 50;
/** Catching a Raf doubles for each one caught inside a single pie. */
export const RAF_POINTS = [200, 400, 800, 1600];
export const START_LIVES = 3;

const READY_SECONDS = 1.9;
const DYING_SECONDS = 1.4;
const CLEAR_SECONDS = 1.8;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, Math.round(Number(n) || 0)));

/**
 * The food on a fresh board, plus however many extra pies were asked for.
 *
 * Extras are spread evenly through the corridor list rather than dropped at
 * random: fifty pies clustered in one quarter of the maze is a different game
 * from fifty spread through it, and only one of them is the one you asked for.
 */
export function freshFood(board, extraPies = 0) {
  const food = new Map();
  const pizzas = [];
  for (let y = 0; y < board.rows; y += 1) {
    for (let x = 0; x < board.cols; x += 1) {
      const ch = board.layout[y][x];
      if (ch === '.') { food.set(key(x, y), 'pizza'); pizzas.push(key(x, y)); }
      else if (ch === 'o') food.set(key(x, y), 'pie');
    }
  }
  const extra = clamp(extraPies, 0, Math.min(MAX_EXTRA_PIES, pizzas.length));
  if (!extra) return food;
  const stride = pizzas.length / extra;
  for (let i = 0; i < extra; i += 1) {
    food.set(pizzas[Math.floor(i * stride + stride / 2)], 'pie');
  }
  return food;
}

/**
 * Where the ghosts stand at the start of a life.
 *
 * They cannot all occupy one tile — fifty ghosts on the spawn square is one
 * ghost as far as the player can see, and they would move as a single block
 * forever. Spread outward through the maze from the spawn point, nearest
 * first, so a crowd fills the middle and spills into the corridors.
 */
export function ghostHome(board, count) {
  const field = board.distanceField(board.rafStart);
  const tiles = board.corridors()
    .map((t) => ({ ...t, d: board.distAt(field, t.x, t.y) }))
    .filter((t) => t.d >= 0)
    .sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
  const out = [];
  for (let i = 0; i < count; i += 1) out.push(tiles[i % tiles.length]);
  return out;
}

/**
 * `player` marks the one Raf a second person drives. Index 0, always, so both
 * phones agree which ghost is whose without sending an id — and so a run with
 * fifty Rafs still has exactly one that is not thinking for itself.
 */
const spawnGhosts = (board, count, playerGhost = false) => ghostHome(board, count)
  .map((tile, i) => ({
    x: tile.x, y: tile.y, dir: DIRS.up, next: null, eaten: false, corner: i,
    player: playerGhost && i === 0,
  }));

export function createGame({
  difficulty = 'hungry', board = 'standard', level = 1, ghosts = 1, extraPies = 0,
  speed = 'normal', playerGhost = false,
} = {}) {
  const b = makeBoard(board);
  const count = clamp(ghosts, 1, MAX_GHOSTS);
  const game = {
    diff: difficultyById(difficulty),
    speed: speedById(speed),
    board: b,
    ghostCount: count,
    playerGhost: !!playerGhost,
    extraPies: clamp(extraPies, 0, MAX_EXTRA_PIES),
    state: 'ready',
    level,
    score: 0,
    lives: START_LIVES,
    food: freshFood(b, extraPies),
    eaten: 0,
    nick: { x: b.nickStart.x, y: b.nickStart.y, dir: DIRS.left, next: null },
    rafs: spawnGhosts(b, count, playerGhost),
    mode: 'guard',
    wave: 'guard',
    modeLeft: 0,
    frightLeft: 0,
    combo: 0,
    timer: READY_SECONDS,
    events: [],
    mouth: 0,
    flash: 0,
    lastScore: 0,
  };
  game.modeLeft = waveSeconds(game.diff, game.level, 'guard');
  return game;
}

/** Start a fresh run with the same setup. */
export function restart(game, overrides = {}) {
  Object.assign(game, createGame({
    difficulty: game.diff.id,
    board: game.board.id,
    speed: game.speed.id,
    ghosts: game.ghostCount,
    extraPies: game.extraPies,
    playerGhost: game.playerGhost,
    level: 1,
    ...overrides,
  }));
  return game;
}

const aheadOf = (board, e, dir) => [board.wrapX(Math.round(e.x) + dir.x), Math.round(e.y) + dir.y];

/** The Raf a second player is driving, if there is one and it is still theirs. */
export const playerRaf = (game) => game.rafs.find((r) => r.player) ?? null;

/**
 * Queue a turn for an entity. Shared by both players, so the ghost handles
 * exactly like Nick does — including the free reversal, which is most of why
 * the controls feel unsticky.
 */
function queueTurn(board, e, dir) {
  if (!e || !dir) return;
  if (sameDir(dir, opposite(e.dir)) && board.walkable(...aheadOf(board, e, dir))) {
    e.dir = dir;
    e.next = null;
    return;
  }
  e.next = dir;
}

/** Queue a turn. It is taken at the next tile centre where it is legal. */
export function steer(game, dir) {
  queueTurn(game.board, game.nick, dir);
}

/** Steer the possessed Raf. A no-op in a one-player game. */
export function steerRaf(game, dir) {
  const raf = playerRaf(game);
  // While eaten it is a pair of eyes finding its way home; the AI drives that
  // leg, and handing back a ghost mid-return would just strand it.
  if (!raf || raf.eaten) return;
  queueTurn(game.board, raf, dir);
}

/**
 * Move one entity `dist` tiles along its direction, one tile centre at a time.
 *
 * Turning happens only exactly on a centre, which keeps everything on the grid
 * no matter how variable the frame time is. Sub-stepping to each centre rather
 * than moving the whole distance at once means a fast entity and a slow frame
 * cannot tunnel through a wall.
 */
function move(board, e, dist, onCentre) {
  let left = dist;
  let guard = 0;
  while (left > 1e-9 && guard < 64) {
    guard += 1;
    const cx = Math.round(e.x);
    const cy = Math.round(e.y);
    if (Math.abs(e.x - cx) < 1e-9 && Math.abs(e.y - cy) < 1e-9) {
      e.x = cx; e.y = cy;
      if (onCentre) onCentre(e, cx, cy);
      if (e.next && board.walkable(...aheadOf(board, e, e.next))) { e.dir = e.next; e.next = null; }
      if (!e.dir || !board.walkable(...aheadOf(board, e, e.dir))) return;
    }
    // The NEXT centre along the direction of travel — floor/ceil, never round.
    // Rounding picks the NEAREST centre, which past the halfway point is the
    // one ahead: the entity then steps to the centre after that and sails past
    // the wall check for the tile it is standing in. That is how Nick ended up
    // at x=57 outside a 19-wide maze.
    let tx = e.x;
    let ty = e.y;
    if (e.dir.x > 0) tx = Math.floor(e.x + 1e-9) + 1;
    else if (e.dir.x < 0) tx = Math.ceil(e.x - 1e-9) - 1;
    else if (e.dir.y > 0) ty = Math.floor(e.y + 1e-9) + 1;
    else ty = Math.ceil(e.y - 1e-9) - 1;

    const remaining = Math.abs(tx - e.x) + Math.abs(ty - e.y);
    if (remaining < 1e-9) return;
    const step = Math.min(left, remaining);
    e.x += e.dir.x * step;
    e.y += e.dir.y * step;
    left -= step;
    if (Math.abs(e.x - tx) < 1e-9 && Math.abs(e.y - ty) < 1e-9) {
      e.x = tx; e.y = ty;
      if (e.x < 0) e.x += board.cols;
      else if (e.x > board.cols - 1) e.x -= board.cols;
    }
  }
}

const touching = (a, b) => Math.abs(a.x - b.x) < 0.7 && Math.abs(a.y - b.y) < 0.7;

function loseLife(game) {
  game.lives -= 1;
  game.state = game.lives > 0 ? 'dying' : 'gameover';
  game.timer = DYING_SECONDS;
  game.events.push(game.lives > 0 ? 'death' : 'gameover');
}

function resetPositions(game) {
  const b = game.board;
  game.nick = { x: b.nickStart.x, y: b.nickStart.y, dir: DIRS.left, next: null };
  game.rafs = spawnGhosts(b, game.ghostCount, game.playerGhost);
  game.mode = 'guard';
  game.wave = 'guard';
  game.modeLeft = waveSeconds(game.diff, game.level, 'guard');
  game.frightLeft = 0;
  game.combo = 0;
}

function nextLevel(game) {
  game.level += 1;
  game.food = freshFood(game.board, game.extraPies);
  game.eaten = 0;
  resetPositions(game);
  game.state = 'ready';
  game.timer = READY_SECONDS;
}

function eatAt(game, x, y) {
  const k = key(x, y);
  const what = game.food.get(k);
  if (!what) return;
  game.food.delete(k);
  game.eaten += 1;
  if (what === 'pizza') {
    game.score += PIZZA_POINTS;
    game.events.push('pizza');
  } else {
    game.score += PIE_POINTS;
    game.combo = 0;
    game.frightLeft = frightSeconds(game.diff, game.level);
    game.mode = 'fright';
    // Turning on the spot is the tell that the rules just changed.
    for (const raf of game.rafs) {
      // Spinning a driven ghost round is yanking the wheel out of someone's
      // hands. They can see the timer; let them decide to run.
      if (raf.eaten || raf.player) continue;
      raf.dir = opposite(raf.dir) ?? raf.dir;
      raf.next = null;
    }
    game.events.push('pie');
  }
  if (!game.food.size) {
    game.state = 'levelclear';
    game.timer = CLEAR_SECONDS;
    game.events.push('levelclear');
  }
}

/** Advance the world. `dt` is seconds, and is clamped by the caller. */
export function update(game, realDt) {
  if (game.state === 'paused' || game.state === 'title' || game.state === 'gameover') return;

  // One factor on the clock rather than on each speed. Scaling only the
  // movement would leave the wave and fright timers running at real speed, so
  // a slower game would cover less maze per wave and quietly become a harder
  // one. Scaling time keeps every relationship in the game identical.
  //
  // The interstitials below are the exception and use REAL seconds: "Ready",
  // the death pause and the level-clear pause are waits, not play. Stretching
  // them to 3.2 seconds on the slow setting makes you wait longer before every
  // single life for no benefit at all — the tempo people asked to change is
  // the one things move at.
  const dt = realDt * (game.speed?.factor ?? 1);

  game.mouth = (game.mouth + dt * 11) % (Math.PI * 2);
  game.flash += dt;

  if (game.state === 'ready') {
    game.timer -= realDt;
    if (game.timer <= 0) game.state = 'playing';
    return;
  }
  if (game.state === 'dying') {
    game.timer -= realDt;
    if (game.timer <= 0) { resetPositions(game); game.state = 'ready'; game.timer = READY_SECONDS; }
    return;
  }
  if (game.state === 'levelclear') {
    game.timer -= realDt;
    if (game.timer <= 0) nextLevel(game);
    return;
  }

  // --- modes -------------------------------------------------------------
  if (game.mode === 'fright') {
    game.frightLeft -= dt;
    if (game.frightLeft <= 0) { game.mode = game.wave; game.frightLeft = 0; game.combo = 0; }
  } else {
    game.modeLeft -= dt;
    if (game.modeLeft <= 0) {
      game.wave = game.wave === 'guard' ? 'chase' : 'guard';
      game.mode = game.wave;
      game.modeLeft = waveSeconds(game.diff, game.level, game.wave);
    }
  }

  // --- Nick --------------------------------------------------------------
  move(game.board, game.nick, NICK_SPEED * dt, (e, cx, cy) => eatAt(game, cx, cy));
  if (game.state !== 'playing') return;

  // --- the ghosts --------------------------------------------------------
  // One field cache for the whole tick: fifty ghosts chasing one target is one
  // breadth-first search, not fifty.
  const fields = new Map();
  for (const raf of game.rafs) {
    // Paying for a turn: a Raf who doubled back at the last junction stands
    // there for a beat before he sets off, which is what makes reversing a
    // decision rather than a free move.
    if (raf.stall > 0) { raf.stall -= dt; continue; }
    const mode = raf.eaten ? 'eaten' : game.mode;
    move(game.board, raf, rafSpeed(game.diff, game.level, mode) * dt, (e, cx, cy) => {
      if (e.eaten && cx === game.board.rafStart.x && cy === game.board.rafStart.y) e.eaten = false;
      // A driven ghost keeps whatever its player queued. Calling decide() here
      // would overwrite their input at every tile centre, which reads as the
      // controls being ignored.
      if (e.player && !e.eaten) return;
      e.next = decide(e, game.nick, e.eaten ? 'eaten' : game.mode, game.diff, game.board, fields, game.food);
    });
  }

  // --- who caught whom ---------------------------------------------------
  for (const raf of game.rafs) {
    if (raf.eaten || !touching(game.nick, raf)) continue;
    if (game.mode === 'fright') {
      const points = RAF_POINTS[Math.min(game.combo, RAF_POINTS.length - 1)];
      game.score += points;
      game.lastScore = points;
      game.combo += 1;
      raf.eaten = true;
      raf.next = null;
      game.events.push('catch');
    } else {
      loseLife(game);
      return;
    }
  }
}

export const piesLeft = (game) => [...game.food.values()].filter((v) => v === 'pie').length;
export const pizzasLeft = (game) => [...game.food.values()].filter((v) => v === 'pizza').length;

/**
 * A board mid-run, for the `/demo` route and the store screenshots.
 *
 * Not a test hook: `/demo` exists so the listing shows the app doing its job
 * rather than an empty first run, and for a game that means a run in progress.
 */
export function seedDemo(game, { flipped = false } = {}) {
  const b = game.board;
  const clear = (x, y) => game.food.delete(key(x, y));
  for (let x = 1; x < b.cols - 1; x += 1) clear(x, b.rows - 2);
  for (let y = b.rows - 6; y <= b.rows - 2; y += 1) clear(6, y);
  for (let x = 1; x <= 8; x += 1) clear(x, b.rows - 8);
  game.eaten = freshFood(b, game.extraPies).size - game.food.size;

  game.state = 'playing';
  game.score = flipped ? 3180 : 1240;
  game.lives = 3;
  game.nick = { x: 5, y: b.rows - 8, dir: DIRS.right, next: null };

  if (flipped) {
    game.rafs = game.rafs.map((r, i) => ({
      ...r, x: 8 + (i % 3), y: b.rows - 8, dir: DIRS.right, eaten: false,
    }));
    game.mode = 'fright';
    game.frightLeft = 4.6;
  } else {
    // Not three tiles away and hunting: the loop keeps running while a
    // screenshot settles, and a seed with a ghost on top of Nick photographs
    // the death animation with a life already gone.
    game.rafs = spawnGhosts(b, game.ghostCount, game.playerGhost);
    game.mode = 'guard';
    game.wave = 'guard';
    game.modeLeft = 30;
  }
  return game;
}

export { DIRS };
